import React, { useRef, useState } from "react";

interface Props {
    disabled?: boolean;
    onComplete: (code: string) => void;
}

export default function OtpInput({ disabled = false, onComplete }: Props) {
    const [code, setCode] = useState<string[]>(Array(6).fill(""));
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const handleChange = (index: number, value: string) => {
        const digit = value.replace(/\D/g, "").slice(-1);

        const newCode = [...code];
        newCode[index] = digit;
        setCode(newCode);

        if (digit && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        if (newCode.every((item) => item !== "")) {
            onComplete(newCode.join(""));
        }
    };

    const handleKeyDown = (
        index: number,
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key === "Backspace" && code[index] === "" && index > 0) {
            const newCode = [...code];
            newCode[index - 1] = "";
            setCode(newCode);
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();

        const pastedData = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, 6);

        if (!pastedData) return;

        const newCode = Array(6).fill("");

        pastedData.split("").forEach((digit, index) => {
            newCode[index] = digit;
        });

        setCode(newCode);

        const nextIndex = Math.min(pastedData.length, 5);
        inputRefs.current[nextIndex]?.focus();

        if (pastedData.length === 6) {
            onComplete(pastedData);
        }
    };

    return (
        <div className="flex items-center justify-center gap-1.5 sm:gap-2">
            {code.map((digit, index) => (
                <input
                    key={index}
                    ref={(element) => {
                        inputRefs.current[index] = element;
                    }}
                    type="text"
                    inputMode="numeric"
                    value={digit}
                    onChange={(event) => handleChange(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    onPaste={handlePaste}
                    maxLength={1}
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    disabled={disabled}
                    className={`h-10 w-9 rounded-xl border text-center text-base font-bold outline-none transition-colors sm:h-11 sm:w-11 sm:text-lg ${
                        digit
                            ? "border-[#186cb2] bg-[#186cb2]/5 text-[#186cb2]"
                            : "border-gray-200 bg-gray-50 text-gray-800"
                    } focus:border-[#186cb2] focus:bg-white focus:ring-4 focus:ring-[#186cb2]/10 disabled:cursor-not-allowed disabled:opacity-60`}
                />
            ))}
        </div>
    );
}